import { apiFetch } from "../apiClient";
import {
  FACTSET_CHART_CREATOR_ERROR_CODES,
  FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES,
  FACTSET_ROUTES,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
} from "./factsetConstants";
import {
  buildPreviewChartPayload,
  type PreviewChartPayloadOptions,
} from "./chartCreatorPayload";
import type {
  FactsetChartParameters,
  FactsetChartPreview,
  FactsetGetStylesetsResult,
  FactsetStyleset,
} from "./chartCreatorTypes";
import { isAbortError } from "../utils/abortError";

const DEFAULT_CHART_CREATOR_TIMEOUT_MS = 60_000;

export class ChartCreatorReauthRequiredError extends Error {
  readonly reauthorizationUrl: string;
  readonly serverErrorCode: string;

  constructor(message: string, reauthorizationUrl: string, serverErrorCode: string) {
    super(message);
    this.name = "ChartCreatorReauthRequiredError";
    this.reauthorizationUrl = reauthorizationUrl;
    this.serverErrorCode = serverErrorCode;
    Object.setPrototypeOf(this, ChartCreatorReauthRequiredError.prototype);
  }
}

export class ChartCreatorFailedError extends Error {
  readonly serverErrorCode?: string;
  readonly httpStatus?: number;
  readonly details?: unknown;

  constructor(
    message: string,
    options: { serverErrorCode?: string; httpStatus?: number; details?: unknown } = {},
  ) {
    super(message);
    this.name = "ChartCreatorFailedError";
    this.serverErrorCode = options.serverErrorCode;
    this.httpStatus = options.httpStatus;
    this.details = options.details;
    Object.setPrototypeOf(this, ChartCreatorFailedError.prototype);
  }
}

export class ChartCreatorTimeoutError extends Error {
  constructor(message = "FactSet Chart Creator request timed out.") {
    super(message);
    this.name = "ChartCreatorTimeoutError";
    Object.setPrototypeOf(this, ChartCreatorTimeoutError.prototype);
  }
}

export interface ChartCreatorRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

interface ParsedApiError {
  error?: string;
  message?: string;
  details?: unknown;
  reauthorizationUrl?: string;
}

function abortError(message: string): DOMException | Error {
  if (typeof DOMException === "function") return new DOMException(message, "AbortError");
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}

function withTimeoutSignal(
  signal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void; didTimeout: () => boolean } {
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) controller.abort(signal.reason);
  else signal?.addEventListener("abort", onAbort, { once: true });
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
    },
    didTimeout: () => timedOut,
  };
}

async function readJsonBounded(response: Response): Promise<unknown> {
  const declaredLength = Number(response.headers.get("content-length"));
  if (
    Number.isFinite(declaredLength) &&
    declaredLength > FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES
  ) {
    throw new ChartCreatorFailedError("FactSet Chart Creator response was too large.", {
      serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.RESPONSE_TOO_LARGE,
      httpStatus: response.status,
    });
  }
  let text: string;
  const reader = response.body?.getReader();
  if (reader) {
    const chunks: Uint8Array[] = [];
    let byteLength = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      byteLength += value.byteLength;
      if (byteLength > FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES) {
        try {
          await reader.cancel();
        } catch {
          // Preserve the bounded-response error if stream cancellation itself fails.
        }
        throw new ChartCreatorFailedError("FactSet Chart Creator response was too large.", {
          serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.RESPONSE_TOO_LARGE,
          httpStatus: response.status,
        });
      }
      chunks.push(value);
    }
    const bytes = new Uint8Array(byteLength);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    text = new TextDecoder().decode(bytes);
  } else {
    text = await response.text();
    if (
      new TextEncoder().encode(text).byteLength >
      FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES
    ) {
      throw new ChartCreatorFailedError("FactSet Chart Creator response was too large.", {
        serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.RESPONSE_TOO_LARGE,
        httpStatus: response.status,
      });
    }
  }
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ChartCreatorFailedError("FactSet Chart Creator returned invalid JSON.", {
      serverErrorCode: FACTSET_CHART_CREATOR_ERROR_CODES.INVALID_RESPONSE,
      httpStatus: response.status,
    });
  }
}

async function throwForResponse(response: Response): Promise<never> {
  let body: ParsedApiError = {};
  try {
    body = (await readJsonBounded(response)) as ParsedApiError;
  } catch (error) {
    if (error instanceof ChartCreatorFailedError) throw error;
  }
  const code = typeof body.error === "string" ? body.error : "";
  const message = body.message || `FactSet Chart Creator request failed (${response.status})`;
  if (REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(code as never)) {
    throw new ChartCreatorReauthRequiredError(
      message,
      body.reauthorizationUrl || FACTSET_ROUTES.OAUTH_START,
      code,
    );
  }
  throw new ChartCreatorFailedError(message, {
    serverErrorCode: code || undefined,
    httpStatus: response.status,
    details: body.details,
  });
}

async function requestJson(
  path: string,
  init: RequestInit,
  options: ChartCreatorRequestOptions = {},
): Promise<unknown> {
  const timeout = withTimeoutSignal(
    options.signal,
    options.timeoutMs ?? DEFAULT_CHART_CREATOR_TIMEOUT_MS,
  );
  try {
    const response = await apiFetch(path, {
      ...init,
      cache: "no-store",
      signal: timeout.signal,
    });
    if (!response.ok) await throwForResponse(response);
    return await readJsonBounded(response);
  } catch (error) {
    if (isAbortError(error)) {
      if (timeout.didTimeout()) {
        throw new ChartCreatorTimeoutError();
      }
      throw abortError("Chart Creator call cancelled by caller.");
    }
    throw error;
  } finally {
    timeout.cleanup();
  }
}

function findObject(
  value: unknown,
  predicate: (value: Record<string, unknown>) => boolean,
  depth = 0,
): Record<string, unknown> | undefined {
  if (depth > 6 || !value || typeof value !== "object") {
    return undefined;
  }
  if (Array.isArray(value)) {
    for (const child of value) {
      const found = findObject(child, predicate, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const object = value as Record<string, unknown>;
  if (predicate(object)) return object;
  for (const child of Object.values(object)) {
    const found = findObject(child, predicate, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function normalizePngData(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;
  return value.startsWith("data:") ? value : `data:image/png;base64,${value}`;
}

function normalizePreviewResponse(payload: unknown): FactsetChartPreview {
  const inner =
    findObject(
      payload,
      (value) =>
        typeof value.fdsChartXml === "string" &&
        value.fdsChartXml.trim().length > 0,
    ) ??
    findObject(
      payload,
      (value) =>
        "fdsChartXml" in value ||
        "error" in value ||
        "pngData" in value ||
        "annotationsJson" in value,
    ) ?? {};
  const fdsChartXml =
    typeof inner.fdsChartXml === "string" ? inner.fdsChartXml : undefined;
  return {
    fdsChartXml,
    pngDataUri:
      typeof inner.pngDataUri === "string"
        ? inner.pngDataUri
        : normalizePngData(inner.pngData),
    annotationsJson:
      typeof inner.annotationsJson === "string" ? inner.annotationsJson : undefined,
    wasStylesetChanged:
      typeof inner.wasStylesetChanged === "boolean"
        ? inner.wasStylesetChanged
        : undefined,
    error:
      inner.error === null
        ? null
        : typeof inner.error === "string"
          ? inner.error
          : !fdsChartXml && typeof inner.message === "string"
            ? inner.message
            : undefined,
  };
}

function coerceStylesets(value: unknown): FactsetStyleset[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const object = entry as Record<string, unknown>;
    return typeof object.id === "string" && typeof object.name === "string"
      ? [{ id: object.id, name: object.name }]
      : [];
  });
}

export async function previewChartViaApi(
  parameters: FactsetChartParameters,
  options: PreviewChartPayloadOptions & ChartCreatorRequestOptions = {},
): Promise<FactsetChartPreview> {
  const response = await requestJson(
    FACTSET_ROUTES.CHART_CREATOR_PREVIEW,
    { method: "POST", body: JSON.stringify(buildPreviewChartPayload(parameters, options)) },
    options,
  );
  return normalizePreviewResponse(response);
}

export async function getStylesetsViaApi(
  options: ChartCreatorRequestOptions = {},
): Promise<FactsetGetStylesetsResult> {
  const response = await requestJson(
    FACTSET_ROUTES.CHART_CREATOR_STYLESETS,
    { method: "GET" },
    options,
  );
  const inner =
    findObject(response, (value) => Array.isArray(value.stylesets)) ?? {};
  return {
    stylesets: coerceStylesets(inner.stylesets),
    currentStylesetId:
      typeof inner.currentStylesetId === "string"
        ? inner.currentStylesetId
        : undefined,
  };
}
